import { useState, useEffect } from 'react'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

const TUTORIAL_MODAL_OPEN_KEY_PREFIX = 'autopilot_tutorial_modal_open'
const TUTORIAL_MODAL_STATE_CHANGE_EVENT = 'tutorialModalStateChange'

export const getTutorialModalOpenKey = (workspaceId: number) => `${TUTORIAL_MODAL_OPEN_KEY_PREFIX}_${workspaceId}`

export const useTutorialModalState = () => {
  const workspaceId = useCurrentWorkspaceId()
  const storageKey = getTutorialModalOpenKey(workspaceId)

  const [isTutorialModalOpen, setIsTutorialModalOpenState] = useState(() => {
    return localStorage.getItem(storageKey) === 'true'
  })

  // Re-sync state when workspaceId changes
  useEffect(() => {
    setIsTutorialModalOpenState(localStorage.getItem(storageKey) === 'true')
  }, [storageKey])

  const setIsTutorialModalOpen = (open: boolean) => {
    setIsTutorialModalOpenState(open)
    if (open) {
      localStorage.setItem(storageKey, 'true')
    } else {
      localStorage.removeItem(storageKey)
    }

    // Dispatch custom event to notify all components
    window.dispatchEvent(
      new CustomEvent(TUTORIAL_MODAL_STATE_CHANGE_EVENT, {
        detail: { isTutorialModalOpen: open, workspaceId },
      }),
    )
  }

  // Listen for storage changes (cross-tab) and custom events (same-tab)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey) {
        setIsTutorialModalOpenState(e.newValue === 'true')
      }
    }

    const handleCustomEvent = (e: CustomEvent) => {
      // Only update if the event is for the same workspace
      if (e.detail.workspaceId === workspaceId) {
        setIsTutorialModalOpenState(e.detail.isTutorialModalOpen)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener(TUTORIAL_MODAL_STATE_CHANGE_EVENT, handleCustomEvent as EventListener)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(TUTORIAL_MODAL_STATE_CHANGE_EVENT, handleCustomEvent as EventListener)
    }
  }, [storageKey, workspaceId])

  return { isTutorialModalOpen, setIsTutorialModalOpen }
}
